import { useState, useEffect } from 'react';
import { Section } from '@components/CompanyOverview/components/Section';
import { Table, Body, OpenNewWindowIcon, Header } from './styled';
import { shallowEqual, useSelector } from 'react-redux';
import { Button } from '@simplywallst/ui-core';
import type { LocationState } from 'redux-first-router';
import type { State as CompanyState } from '@/components/Company/redux/interface';
import type { People } from '@ducks/people/reducers';
import { useParagrapher } from '@/hooks/useParagrapher';
import { separateByThreeDigit } from '@simplywallst/formatting';
import ContentLoader from 'react-content-loader';
import { useTheme } from '@simplywallst/ui-core';
import PageAnchor from '@components/PageAnchor';
import { COMPANY_ABOUT_ANCHOR } from '@/constants/anchors';
import { pathConstructor } from '@/hooks/useCompany/utils';
import Media from 'react-media';
import { useMobileDetect } from '@/hooks/useMobileDetect';
import { getViewMode } from '@/redux/location/selectors';
import { ViewMode } from '@/components/Company/enum';

interface State {
  location: LocationState;
  company: CompanyState;
  people: Record<string, People>;
}
interface Selected {
  founded: string;
  employees: string;
  ceo: string;
  industry: string;
  website?: string;
  description: string;
}

export function AboutTheCompany() {
  const theme = useTheme();
  const isMobile = useMobileDetect();
  const viewMode = useSelector(getViewMode);
  const [expanded, setExpanded] = useState(false);
  const state = useSelector<State, Selected | undefined>((state) => {
    try {
      const path = pathConstructor(state.location);
      const companyId = state.company.urlToIdMapping[path];
      const summary = state.company.summary[companyId];
      const management = state.company.management[companyId];
      const analysis = state.company.analysis[companyId];
      let ceoName = 'n/a';
      if (management.leaderId !== '') {
        const ceo = state.people[management.leaderId];
        ceoName = `${ceo.salutation} ${ceo.lastName}`;
      }
      let yearFounded = 'n/a';
      if (!isNaN(summary.yearFounded)) {
        yearFounded = summary.yearFounded.toString();
      }
      return {
        founded: yearFounded,
        employees:
          typeof analysis?.totalEmployees === 'number'
            ? separateByThreeDigit(analysis?.totalEmployees)
            : 'n/a',
        industry: summary.industryData.primaryName,
        ceo: ceoName,
        website: summary.website,
        description: summary.description,
      };
    } catch {
      return undefined;
    }
  }, shallowEqual);
  const paragraphs = useParagrapher(state?.description);

  //Auto expand when anchor changes to #about
  useEffect(() => {
    const expandHandler = () => {
      if (window.location.hash === `#${COMPANY_ABOUT_ANCHOR}`)
        setExpanded(true);
    };

    expandHandler();
    window.addEventListener('hashchange', expandHandler);
    return () => window.removeEventListener('hashchange', expandHandler);
  }, [setExpanded]);

  useEffect(() => {
    if (viewMode === ViewMode.Print) setExpanded(true);
  }, [viewMode]);

  //Remove #about anchor on collapse
  const handleToggle = () => {
    if (expanded && window.location.hash === `#${COMPANY_ABOUT_ANCHOR}`) {
      window.history.replaceState(null, document.title, ' ');
    }
    setExpanded(!expanded);
  };

  const websiteLink =
    state && typeof state.website !== 'undefined' ? (
      <a
        href={state.website}
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        {state.website}
        <OpenNewWindowIcon />
      </a>
    ) : (
      'n/a'
    );

  return (
    <>
      <PageAnchor id={COMPANY_ABOUT_ANCHOR} />
      <Section>
        <Header>About the Company</Header>
        <Table>
          <thead>
            <tr>
              <th>Founded</th>
              <th>Employees</th>
              <th>CEO</th>
              <Media
                query={{ minWidth: theme.smMinRaw }}
                defaultMatches={!isMobile}
                render={() => <th>Website</th>}
              />
            </tr>
          </thead>
          <tbody>
            {typeof state === 'undefined' ? (
              <tr>
                <td>
                  <ContentLoader
                    foregroundColor={theme.color.spaceGray}
                    backgroundColor={theme.color.darkGray}
                    height="14"
                    width="100"
                  >
                    <rect x="0" y="0" rx="1" ry="1" width="100" height="14" />
                  </ContentLoader>
                </td>
                <td>
                  <ContentLoader
                    foregroundColor={theme.color.spaceGray}
                    backgroundColor={theme.color.darkGray}
                    height="14"
                    width="100"
                  >
                    <rect x="0" y="0" rx="1" ry="1" width="100" height="14" />
                  </ContentLoader>
                </td>
                <td>
                  <ContentLoader
                    foregroundColor={theme.color.spaceGray}
                    backgroundColor={theme.color.darkGray}
                    height="14"
                    width="100"
                  >
                    <rect x="0" y="0" rx="1" ry="1" width="100" height="14" />
                  </ContentLoader>
                </td>
                <td>
                  <ContentLoader
                    foregroundColor={theme.color.spaceGray}
                    backgroundColor={theme.color.darkGray}
                    height="14"
                    width="100"
                  >
                    <rect x="0" y="0" rx="1" ry="1" width="100" height="14" />
                  </ContentLoader>
                </td>
              </tr>
            ) : (
              <tr>
                <td>{state.founded}</td>
                <td>{state.employees}</td>
                <td>{state.ceo}</td>
                <Media
                  query={{ minWidth: theme.smMinRaw }}
                  defaultMatches={!isMobile}
                  render={() => <td>{websiteLink}</td>}
                />
              </tr>
            )}
          </tbody>
        </Table>
        <Media
          query={{ maxWidth: theme.xsMaxRaw }}
          defaultMatches={isMobile}
          render={() => (
            <Table>
              <thead>
                <tr>
                  <th>Website</th>
                </tr>
              </thead>
              <tbody>
                {typeof state === 'undefined' ? (
                  <tr>
                    <td>
                      <ContentLoader
                        foregroundColor={theme.color.spaceGray}
                        backgroundColor={theme.color.darkGray}
                        height="14"
                        width="100"
                      >
                        <rect
                          x="0"
                          y="0"
                          rx="1"
                          ry="1"
                          width="100"
                          height="14"
                        />
                      </ContentLoader>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td>{websiteLink}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        />

        {paragraphs.map((paragraph, index) => {
          if (expanded) {
            return <Body key={index}>{paragraph}</Body>;
          } else {
            if (index === 0) {
              return (
                <Body key={index} clamped>
                  {paragraph}
                </Body>
              );
            } else {
              return null;
            }
          }
        })}
        <Button
          displayXsDown="block"
          onClick={handleToggle}
          styleType="loud"
          data-cy-id="company-description-toggle"
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      </Section>
    </>
  );
}
